/**
 * Data access layer — switches between mock data and real GraphQL API
 * based on the VITE_MOCK_DATA environment variable.
 *
 * All functions are async. When VITE_MOCK_DATA=true, resolves immediately
 * with local mock data. When false, calls the AppSync GraphQL API.
 */

import {
  MOCK_ENTITIES,
  getStudentsForEntity as mockGetStudentsForEntity,
  getContactsForStudent as mockGetContactsForStudent,
  getStudent as mockGetStudent,
  getEntity as mockGetEntity,
  type Entity,
  type Student,
  type Contact,
} from './mock-data';
import { graphqlRequest } from './graphql-client';
import { GET_USER_PROFILE, GET_CURRENT_USER_PROFILE, GET_CONTACTS, GET_ENTITIES, GET_STUDENTS_BY_ENTITY } from './graphql/queries';
import { CREATE_CONTACT, UPDATE_CONTACT, DELETE_CONTACT, SET_PRIMARY_CONTACT, REORDER_CONTACTS } from './graphql/mutations';

export type { Entity, Student, Contact };

const isMockData = import.meta.env.VITE_MOCK_DATA === 'true';

/* ── Helper to get ID token from session storage (OIDC client stores it) ── */
function getIdToken(): string {
  // oidc-client-ts stores the user object in sessionStorage keyed by authority+client_id
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key?.startsWith('oidc.user:')) {
      try {
        const data = JSON.parse(sessionStorage.getItem(key) || '');
        if (data.id_token) return data.id_token;
      } catch { /* ignore */ }
    }
  }
  return '';
}

/* ── GraphQL response types (matching schema) ── */

interface GqlContactValue {
  val?: string;
  building?: string;
  unit?: string;
  streetNumber?: string;
  streetName?: string;
  locality?: string;
  region?: string;
  postcode?: string;
  country?: string;
  formattedAddress?: string;
}

interface GqlUserContact {
  id: string;
  userId: string;
  typeId: number;
  rank: number;
  contactValue: GqlContactValue;
  isActive: boolean;
  isRecovery: boolean;
  bounced: boolean;
  bounceCount: number;
}

interface GqlUserProfile {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  gender?: string;
  birthDate?: string;
  isActive: boolean;
  entityId: string;
  contacts?: GqlUserContact[];
}

/** Map a GraphQL contact to the local Contact shape */
function mapContact(c: GqlUserContact): Contact {
  return {
    id: c.id,
    studentId: c.userId,
    typeId: c.typeId,
    rank: c.rank,
    contactValue: {
      val: c.contactValue.val,
      formattedAddress: c.contactValue.formattedAddress,
      building: c.contactValue.building,
      unit: c.contactValue.unit,
      streetNumber: c.contactValue.streetNumber,
      streetName: c.contactValue.streetName,
      locality: c.contactValue.locality,
      region: c.contactValue.region,
      postcode: c.contactValue.postcode,
      country: c.contactValue.country,
    },
    isActive: c.isActive,
    isRecovery: c.isRecovery,
    bounced: c.bounced,
    bounceCount: c.bounceCount,
  };
}

/** Map a GraphQL profile to the local Student shape */
function mapProfile(p: GqlUserProfile): Student {
  return {
    id: p.id,
    studentId: p.id,
    entityId: p.entityId,
    firstName: p.firstName,
    lastName: p.lastName,
    email: '', // populated from contacts if needed
    status: p.isActive ? 'active' : 'inactive',
    enrollmentDate: '',
    contactCount: p.contacts?.length ?? 0,
  };
}

/* ── Entities ── */

export function getEntities(): Entity[] {
  return MOCK_ENTITIES;
}

/** Async: fetch entities from GraphQL */
export async function fetchEntities(): Promise<Entity[]> {
  if (isMockData) return MOCK_ENTITIES;
  const token = getIdToken();
  if (!token) return [];
  const data = await graphqlRequest<{ entities: Array<{ id: string; name: string; code?: string; isActive: boolean; studentCount: number }> }>(
    GET_ENTITIES,
    {},
    token,
  );
  return data.entities.map((e) => ({
    id: e.id,
    name: e.name,
    studentCount: e.studentCount,
  }));
}

export function getEntity(entityId: string): Entity | undefined {
  if (isMockData) return mockGetEntity(entityId);
  // TODO: Add entity query to GraphQL schema when needed
  return mockGetEntity(entityId);
}

/* ── Students / Profiles ── */

export function getStudentsForEntity(entityId: string): Student[] {
  if (isMockData) return mockGetStudentsForEntity(entityId);
  return [];
}

/** Async: fetch students for an entity from GraphQL */
export async function fetchStudentsByEntity(entityId: string): Promise<Student[]> {
  if (isMockData) return mockGetStudentsForEntity(entityId);
  const token = getIdToken();
  if (!token) return [];

  interface GqlStudentProfile {
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;
    gender?: string;
    birthDate?: string;
    isActive: boolean;
    entityId: string;
    contacts?: Array<{ id: string }>;
  }

  const data = await graphqlRequest<{ studentsByEntity: GqlStudentProfile[] }>(
    GET_STUDENTS_BY_ENTITY,
    { entityId },
    token,
  );

  return data.studentsByEntity.map((p) => ({
    id: p.id,
    studentId: p.id,
    entityId: p.entityId,
    firstName: p.firstName,
    lastName: p.lastName,
    email: '',
    status: p.isActive ? 'active' as const : 'inactive' as const,
    enrollmentDate: '',
    contactCount: p.contacts?.length ?? 0,
  }));
}

export function getStudent(studentId: string): Student | undefined {
  if (isMockData) return mockGetStudent(studentId);
  return undefined;
}

/** Async: fetch a user profile from GraphQL */
export async function fetchUserProfile(userId: string): Promise<Student | undefined> {
  if (isMockData) return mockGetStudent(userId);
  const token = getIdToken();
  if (!token) return undefined;
  const data = await graphqlRequest<{ getUserProfile: GqlUserProfile | null }>(
    GET_USER_PROFILE,
    { userId },
    token,
  );
  return data.getUserProfile ? mapProfile(data.getUserProfile) : undefined;
}

/** Async: fetch the current user's own profile */
export async function fetchCurrentUserProfile(): Promise<Student | undefined> {
  if (isMockData) return undefined;
  const token = getIdToken();
  if (!token) return undefined;
  const data = await graphqlRequest<{ getCurrentUserProfile: GqlUserProfile | null }>(
    GET_CURRENT_USER_PROFILE,
    {},
    token,
  );
  return data.getCurrentUserProfile ? mapProfile(data.getCurrentUserProfile) : undefined;
}

/* ── Contacts ── */

export function getContactsForStudent(studentId: string): Contact[] {
  if (isMockData) return mockGetContactsForStudent(studentId);
  return [];
}

/** Async: fetch contacts from GraphQL */
export async function fetchContacts(
  userId: string,
  opts?: { typeId?: number; activeOnly?: boolean },
): Promise<Contact[]> {
  if (isMockData) return mockGetContactsForStudent(userId);
  const token = getIdToken();
  if (!token) return [];
  const data = await graphqlRequest<{ contacts: GqlUserContact[] }>(
    GET_CONTACTS,
    { userId, typeId: opts?.typeId ?? null, activeOnly: opts?.activeOnly ?? null },
    token,
  );
  return data.contacts.map(mapContact);
}

/** Async: create a new contact */
export async function createContact(input: {
  userId: string;
  typeId: number;
  val?: string;
  building?: string;
  unit?: string;
  streetNumber?: string;
  streetName?: string;
  locality?: string;
  region?: string;
  postcode?: string;
  country?: string;
  isActive?: boolean;
  isRecovery?: boolean;
}): Promise<Contact> {
  const token = getIdToken();
  const data = await graphqlRequest<{ createContact: GqlUserContact }>(
    CREATE_CONTACT,
    { input },
    token,
  );
  return mapContact(data.createContact);
}

/** Async: update an existing contact */
export async function updateContact(
  id: string,
  input: {
    val?: string;
    building?: string;
    unit?: string;
    streetNumber?: string;
    streetName?: string;
    locality?: string;
    region?: string;
    postcode?: string;
    country?: string;
    isActive?: boolean;
    isRecovery?: boolean;
  },
): Promise<Contact> {
  const token = getIdToken();
  const data = await graphqlRequest<{ updateContact: GqlUserContact }>(
    UPDATE_CONTACT,
    { id, input },
    token,
  );
  return mapContact(data.updateContact);
}

/** Async: delete a contact */
export async function deleteContact(id: string): Promise<boolean> {
  const token = getIdToken();
  const data = await graphqlRequest<{ deleteContact: boolean }>(
    DELETE_CONTACT,
    { id },
    token,
  );
  return data.deleteContact;
}

/** Async: reorder contacts by providing ordered list of IDs */
export async function reorderContacts(contactIds: string[]): Promise<void> {
  const token = getIdToken();
  await graphqlRequest(
    REORDER_CONTACTS,
    { input: { contactIds } },
    token,
  );
}

/** Async: promote a contact to rank 1 (primary) for its type */
export async function setPrimaryContact(id: string): Promise<Contact> {
  const token = getIdToken();
  const data = await graphqlRequest<{ setPrimaryContact: GqlUserContact }>(
    SET_PRIMARY_CONTACT,
    { id },
    token,
  );
  return mapContact(data.setPrimaryContact);
}
